<template>
  <ReportWrapper>
    <template #actions>
      <div class="flex gap-x-4 mb-5">
        <CfsDateField
          v-model="selectedDateRange"
          label="Date Range"
          placeholder="Select date range"
          range
          :rules="[rules.dateRangeMaxRange(MAX_DAYS_RANGE)]"
          :hide-details="false"
          class="w-80"
        />

        <CfsAutocomplete
          v-model="selectedDestinations"
          :items="destinations"
          :label="`Select destinations (${destinations?.length || 0})`"
          placeholder="Search destinations..."
          :disabled="!destinations || destinations.length === 0"
          item-text="code"
          item-value="code"
          multiple
          chips
          clearable
          outlined
          return-object
          class="w-80"
        >
          <template #item="{ item }">
            <div class="flex justify-between items-center w-full">
              <span class="text-sm font-medium">{{ item.code }}</span>
              <!-- <v-chip size="small" color="primary" variant="outlined">
                {{ item.count }}
              </v-chip> -->
            </div>
          </template>
        </CfsAutocomplete>
        <CfsTextField v-model="searchInput" label="Search" :value="searchInput" />
      </div>

      <div class="flex gap-x-2 grow-0">
        <v-btn
          color="primary"
          :disabled="!hasSelectedData"
          :loading="isFinalizing"
          @click="onFinalize"
        >
          Finalize
        </v-btn>
        <v-btn
          color="primary"
          :disabled="!selectedCustomers.length"
          :loading="isDownloading"
          @click="onDownloadReport"
        >
          <v-icon size="1rem" class="mr-1">mdi-tray-arrow-down</v-icon>
          <span>Download</span>
        </v-btn>
      </div>
    </template>

    <div
      v-if="(!filteredCustomers || filteredCustomers.length === 0) && !isLoadingCustomers"
      class="text-center py-8 text-gray-500"
    >
      <v-icon size="48" color="gray" class="mb-2">mdi-alert-circle-outline</v-icon>
      <p class="text-lg font-medium mb-2">No customers available</p>
      <p class="text-sm">
        IPI Weekly Billing is not configured. Please contact administrator to set up charge codes.
      </p>
    </div>
    <CfsTable
      v-else
      class="charge-table"
      :headers="IpiWeeklyBillingTableHeaders"
      v-bind="$attrs"
      :items="filteredCustomers"
      :loading="isLoadingCustomers"
      :value="selectedCustomers"
      :show-select="true"
      item-key="_uiid"
      hide-default-footer
      :items-per-page="-1"
      tr-background
      @input="onSelectCustomers"
    >
      <template #item.data-table-select="{ isSelected, select }">
        <v-simple-checkbox :value="isSelected" color="primary" @input="select" />
      </template>

      <template #header.data-table-select="{ props, on }">
        <v-simple-checkbox v-bind="props" color="primary" v-on="on" />
      </template>

      <template #item.status.description="{ item }">
        <v-chip
          :color="getStatusColor(item.status.code).color"
          :variant="getStatusColor(item.status.code).variant"
          size="small"
          :prepend-icon="getStatusColor(item.status.code).icon"
        >
          {{ item.status.description }}
        </v-chip>
      </template>
    </CfsTable>
  </ReportWrapper>
</template>

<script lang="ts">
import { defineComponent, watch } from 'vue';
import ReportWrapper from '@/components/reports/ReportWrapper.vue';
import { DataTableHeader } from 'vuetify';
import { useWeeklyBilling, getStatusColor } from '@/composables/reports/useWeeklyBilling';
import { useState } from '@/composables/common/useState';
import { useToast } from '@/composables/useToast';
import rules from '@/helpers/rules';
import dayjs from 'dayjs';

export const IpiWeeklyBillingTableHeaders: DataTableHeader[] = [
  { text: 'Account Number', value: 'customer.accountNumber' },
  { text: 'Account Name', value: 'customer.accountName' },
  { text: 'Destination', value: 'destination' },
  { text: 'Finalization Status', value: 'status.description', sortable: false },
  { text: '', value: 'view', sortable: false },
];

export default defineComponent({
  name: 'IpiWeeklyBillingReport',
  components: {
    ReportWrapper,
  },
  setup() {
    const [selectedDateRange] = useState<{ start: string; end: string } | null>(null);
    const toast = useToast();
    const MAX_DAYS_RANGE = 7;

    const {
      customers,
      filteredCustomers,
      destinations,
      selectedCustomers,
      selectedDestinations,
      isLoadingCustomers,
      isDownloading,
      isFinalizing,
      hasSelectedData,
      searchInput,
      loadData,
      downloadReport,
      finalizeCharges,
      clearForm,
    } = useWeeklyBilling();

    const onChangeDateRange = async () => {
      if (!selectedDateRange.value) {
        return;
      }
      const { start, end } = selectedDateRange.value;
      const diffDays = dayjs(end).diff(dayjs(start), 'day');

      if (diffDays > MAX_DAYS_RANGE) {
        toast.error(`Date range should not exceed ${MAX_DAYS_RANGE} days.`);
        return;
      }

      await loadData(selectedDateRange.value);
    };

    const onDownloadReport = async () => {
      if (!selectedDateRange.value) return;

      if (selectedCustomers.value.length === 0) {
        toast.error('Please select at least one customer');
        return;
      }

      await downloadReport(selectedDateRange.value);
    };

    const onFinalize = async () => {
      if (!selectedDateRange.value) return;

      await finalizeCharges(selectedDateRange.value);
    };

    const onSelectCustomers = (customers: any[]) => {
      selectedCustomers.value = customers;
    };

    const getItemKey = (item: any) => {
      return `${item.customer.id}-${item.destination}`;
    };

    watch(selectedDateRange, (newValue) => {
      if (newValue) {
        onChangeDateRange();
      } else {
        clearForm();
      }
    });

    return {
      selectedDateRange,
      customers,
      filteredCustomers,
      destinations,
      selectedCustomers,
      selectedDestinations,
      isLoadingCustomers,
      isDownloading,
      isFinalizing,
      hasSelectedData,
      rules,
      searchInput,
      getItemKey,
      onChangeDateRange,
      onDownloadReport,
      onFinalize,
      onSelectCustomers,
      getStatusColor,
      IpiWeeklyBillingTableHeaders,
      MAX_DAYS_RANGE,
    };
  },
});
</script>
